interface SEODataProps {
  pageType: 'home' | 'category' | 'article' | 'about';
  title: string;
  description: string;
  canonical: string;
  articleData?: {
    headline: string;
    datePublished: string;
    dateModified: string;
    author: string;
    image: string;
  };
}

export default function SEOData({ pageType, title, description, canonical, articleData }: SEODataProps) {
  const baseUrl = 'https://longjinman.com';

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '龙禁漫',
    url: baseUrl,
    description: '龙禁漫是专注于正版漫画内容的综合平台，提供海量漫画资源在线阅读与下载。',
    sameAs: [
      'https://weibo.com/longjinman',
      'https://space.bilibili.com/longjinman',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'service@longjinman.com',
      availableLanguage: ['Chinese'],
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '龙禁漫',
    url: baseUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl}/category?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: canonical,
    isPartOf: { '@type': 'WebSite', name: '龙禁漫', url: baseUrl },
  };

  const breadcrumbListSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: pageType === 'article'
      ? [
          { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
          { '@type': 'ListItem', position: 2, name: '分类浏览', item: `${baseUrl}/category` },
          { '@type': 'ListItem', position: 3, name: '漫画详情' },
        ]
      : pageType === 'category'
      ? [
          { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
          { '@type': 'ListItem', position: 2, name: '分类浏览' },
        ]
      : pageType === 'about'
      ? [
          { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ]
      : [{ '@type': 'ListItem', position: 1, name: '首页' }],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '龙禁漫提供哪些类型的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '龙禁漫涵盖热血、恋爱、悬疑、奇幻、搞笑、科幻、恐怖、武侠、日常等九大漫画分类，满足不同读者的阅读偏好。',
        },
      },
      {
        '@type': 'Question',
        name: '龙禁漫的漫画资源是否正版？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '龙禁漫与多家国内外知名漫画出版社和作者建立了正版授权合作关系，平台所有漫画内容均为正版授权，确保读者享受高质量的阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在龙禁漫上找到想看的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页的分类导航、搜索功能以及热门推荐来发现感兴趣的漫画。龙禁漫提供按题材、热度、更新时间等多种方式浏览漫画资源。',
        },
      },
      {
        '@type': 'Question',
        name: '龙禁漫是否支持离线阅读？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '龙禁漫支持漫画下载功能，您可以将喜欢的漫画下载到本地，在没有网络的情况下也能畅快阅读。',
        },
      },
      {
        '@type': 'Question',
        name: '龙禁漫的更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '龙禁漫平台上的热门连载漫画通常保持每周更新的频率，部分作品甚至每日更新，确保读者能够第一时间追更喜爱的漫画。',
        },
      },
    ],
  };

  const schemas = [organizationSchema, websiteSchema, webpageSchema, breadcrumbListSchema];

  if (pageType === 'home') {
    schemas.push(faqSchema);
  }

  if (pageType === 'article' && articleData) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: articleData.headline,
      datePublished: articleData.datePublished,
      dateModified: articleData.dateModified,
      author: {
        '@type': 'Person',
        name: articleData.author,
      },
      image: articleData.image,
      publisher: {
        '@type': 'Organization',
        name: '龙禁漫',
        url: baseUrl,
      },
    });
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schemas) }}
    />
  );
}